import {
  CalculateFunction,
  FieldValues
} from 'interfaces/constants/CalculateModalConfig';
import { CalculateFieldNames } from 'utils/constants/fieldNames';
import { roundToDecimal, truncateToDecimal } from './truncateToDecimal';
import { isEmpty } from './isEmpty';

type DivisionConfig = {
  numerator: string;
  denominator: string;
};

export const formatCalculationResult = (
  value: number,
  decimalPlaces: number = 0,
  isRounded: boolean = false
): string | null => {
  if (!isFinite(value) || isNaN(value)) {
    return null;
  }

  if (Number.isInteger(value)) {
    return value.toString();
  }

  const result = isRounded
    ? roundToDecimal(value, decimalPlaces)
    : truncateToDecimal(value, decimalPlaces);
  return result.toString();
};

export const calculateOPEXPercent = (value: string, revenue: string) => {
  if (value) {
    const truncatedValue = (Number(value) / Number(revenue)) * 100;
    return formatCalculationResult(truncatedValue);
  }
  return null;
};

export const calculateOPEXDollar = (value: string, revenue: string) => {
  if (value) {
    const truncatedValue = (Number(revenue) * Number(value)) / 100;
    return formatCalculationResult(truncatedValue);
  }
  return null;
};

export const calculateHeadcountPercent = (
  headcount: string,
  totalHeadcount: string
) => {
  if (headcount && totalHeadcount && Number(totalHeadcount) > 0) {
    const truncatedValue = (Number(headcount) / Number(totalHeadcount)) * 100;
    return formatCalculationResult(truncatedValue);
  }
  return null;
};

const calculateDivision = (
  values: FieldValues,
  config: DivisionConfig,
  isPercent: boolean = false,
  isRounded: boolean = false,
  decimalPlaces: number = 0
): string | null => {
  const numeratorValue = values[config.numerator];
  const denominatorValue = values[config.denominator];

  if (isEmpty(numeratorValue) || isEmpty(denominatorValue) || denominatorValue === '0') {
    return null;
  }

  const result =
    (Number(numeratorValue) / Number(denominatorValue)) * (isPercent ? 100 : 1);
  return formatCalculationResult(result, decimalPlaces, isRounded);
};

const calculateSum = (values: FieldValues, decimalPlaces: number = 0) => {
  const hasEmpty = Object.values(values).some(val => isEmpty(val));
  if (hasEmpty) return null;

  const result = Object.values(values).reduce((acc, val) => {
    return acc + Number(val);
  }, 0);

  return formatCalculationResult(result, decimalPlaces);
};

export const calculateArrPerFte: CalculateFunction = (values: FieldValues) => {
  const numerator = Number(values[CalculateFieldNames.CURRENT_ARR])
    ? CalculateFieldNames.CURRENT_ARR
    : CalculateFieldNames.ANNUAL_REVENUE;

  const result = calculateDivision(values, {
    numerator,
    denominator: CalculateFieldNames.TOTAL_HEADCOUNT
  });

  return result;
};

export const calculateRuleOf40: CalculateFunction = (values: FieldValues) => {
  const result = calculateSum({
    a: values[CalculateFieldNames.FCF_MARGIN],
    b: values[CalculateFieldNames.YOY_ARR]
  });

  return result;
};

export const calculateNetNewArr: CalculateFunction = (values: FieldValues) => {
  const result = calculateSum({
    a: values[CalculateFieldNames.NEW_LOGO_ARR],
    b: values[CalculateFieldNames.EXPANSION_ARR],
    c: values[CalculateFieldNames.GROSS_CHURN_ARR]
  });

  return result;
};

export const calculateEOPArr: CalculateFunction = (values: FieldValues) => {
  const result = calculateSum({
    a: values[CalculateFieldNames.BOP_ARR],
    b: values[CalculateFieldNames.NEW_LOGO_ARR],
    c: values[CalculateFieldNames.EXPANSION_ARR],
    d: values[CalculateFieldNames.GROSS_CHURN_ARR]
  });

  return result;
};

export const calculateQuickRatio: CalculateFunction = (values: FieldValues) => {
  const newLogoArr = values[CalculateFieldNames.NEW_LOGO_ARR];
  const expansionArr = values[CalculateFieldNames.EXPANSION_ARR];
  const grossChurnArr = values[CalculateFieldNames.GROSS_CHURN_ARR];
  if (!newLogoArr || !expansionArr || !grossChurnArr || grossChurnArr === '0')
    return null;

  const result = formatCalculationResult(
    (Number(newLogoArr) + Number(expansionArr)) / Math.abs(Number(grossChurnArr)),
    1
  );
  return result;
};

const calculateEOPArrValue = (values: FieldValues): string | null => {
  const eopArr = values[CalculateFieldNames.EOP_ARR];
  if (eopArr) return eopArr;

  return calculateEOPArr(values) ?? null;
};

const calculateNetNewArrValue = (values: FieldValues): string | null => {
  const netNewArr = values[CalculateFieldNames.NET_NEW_ARR];
  if (netNewArr) return netNewArr;

  return calculateNetNewArr(values) ?? null;
};

export const calculateNetDollarRetention: CalculateFunction = (
  values: FieldValues
) => {
  const bopArr = values[CalculateFieldNames.BOP_ARR];
  const newLogoArr = values[CalculateFieldNames.NEW_LOGO_ARR];
  const eopArr = calculateEOPArrValue(values);
  const netNewArr = calculateNetNewArrValue(values);

  if (
    !bopArr ||
    !newLogoArr ||
    !eopArr ||
    !netNewArr ||
    (bopArr === '0' && eopArr === '0')
  )
    return null;

  const result = formatCalculationResult(
    (1 +
      (Number(netNewArr) - Number(newLogoArr)) /
        ((Number(bopArr) + Number(eopArr)) / 2)) *
      100
  );

  return result;
};

export const calculateGrossDollarRetention: CalculateFunction = (
  values: FieldValues
) => {
  const grossChurnArr = values[CalculateFieldNames.GROSS_CHURN_ARR];
  const bopArr = values[CalculateFieldNames.BOP_ARR];
  const eopArr = calculateEOPArrValue(values);

  if (
    isEmpty(grossChurnArr) ||
    isEmpty(bopArr) ||
    isEmpty(eopArr) ||
    (bopArr === '0' && eopArr === '0')
  )
    return null;

  const result = formatCalculationResult(
    (1 - Math.abs(Number(grossChurnArr)) / ((Number(bopArr) + Number(eopArr)) / 2)) * 100
  );
  return result;
};

export const calculateNewLogoArrOfGrossNew: CalculateFunction = (
  values: FieldValues
) => {
  const newLogoArr = values[CalculateFieldNames.NEW_LOGO_ARR];
  const expansionArr = values[CalculateFieldNames.EXPANSION_ARR];
  if (
    !newLogoArr ||
    !expansionArr ||
    (newLogoArr === '0' && expansionArr === '0')
  )
    return null;

  const result = formatCalculationResult(
    (Number(newLogoArr) / (Number(newLogoArr) + Number(expansionArr))) * 100,
    0,
    true
  );
  return result;
};

export const calculateExpansionArrOfGrossNew: CalculateFunction = (
  values: FieldValues
) => {
  const newLogoArr = values[CalculateFieldNames.NEW_LOGO_ARR];
  const expansionArr = values[CalculateFieldNames.EXPANSION_ARR];
  const newLogoArrOfGrossNew =
    values[CalculateFieldNames.NEW_LOGO_ARR_OF_GROSS_NEW];
  if (
    !newLogoArr ||
    !expansionArr ||
    !newLogoArrOfGrossNew ||
    (newLogoArr === '0' && expansionArr === '0')
  )
    return null;

  const result = formatCalculationResult(100 - Number(newLogoArrOfGrossNew));
  return result;
};

export const calculateBurnMultiple: CalculateFunction = (
  values: FieldValues
) => {
  const fcf = Number(values[CalculateFieldNames.FCF]);
  const netNewArr = Number(values[CalculateFieldNames.NET_NEW_ARR]);

  if (!fcf || fcf > 0 || !netNewArr || netNewArr < 0 || netNewArr === 0)
    return null;

  const division = -fcf / netNewArr;

  const result = formatCalculationResult(division, 1);

  return result;
};

export const calculateNetMagicNumber: CalculateFunction = (
  values: FieldValues
) => {
  const netNewArr = calculateNetNewArrValue(values);

  const divisionValues = {
    ...values,
    [CalculateFieldNames.NET_NEW_ARR]: netNewArr
  };

  const result = calculateDivision(
    divisionValues,
    {
      numerator: CalculateFieldNames.NET_NEW_ARR,
      denominator: CalculateFieldNames.SM_OPEX
    },
    false,
    false,
    1
  );
  return result;
};

export const calculateOpexPerFte: CalculateFunction = (values: FieldValues) => {
  const result = calculateDivision(
    values,
    {
      numerator: CalculateFieldNames.TOTAL_OPEX,
      denominator: CalculateFieldNames.TOTAL_HEADCOUNT
    },
    false
  );

  return result;
};

export const calculateRDHeadPercent: CalculateFunction = (
  values: FieldValues
) => {
  const fteValue = values[CalculateFieldNames.RD_HEADCOUNT];
  if (!fteValue) return null;

  const result = calculateDivision(
    values,
    {
      numerator: CalculateFieldNames.RD_HEADCOUNT,
      denominator: CalculateFieldNames.TOTAL_HEADCOUNT
    },
    true,
    true
  );
  return result;
};

export const calculateSMHeadPercent: CalculateFunction = (
  values: FieldValues
) => {
  const fteValue = values[CalculateFieldNames.SM_HEADCOUNT];
  if (!fteValue) return null;

  const result = calculateDivision(
    values,
    {
      numerator: CalculateFieldNames.SM_HEADCOUNT,
      denominator: CalculateFieldNames.TOTAL_HEADCOUNT
    },
    true,
    true
  );
  return result;
};

export const calculateGAHeadPercent: CalculateFunction = (
  values: FieldValues
) => {
  const fteValue = values[CalculateFieldNames.GA_HEADCOUNT];
  if (!fteValue) return null;

  const result = calculateDivision(
    values,
    {
      numerator: CalculateFieldNames.GA_HEADCOUNT,
      denominator: CalculateFieldNames.TOTAL_HEADCOUNT
    },
    true,
    true
  );
  return result;
};

export const calculateGAOpex: CalculateFunction = (values: FieldValues) => {
  const result = calculateDivision(
    values,
    {
      numerator: CalculateFieldNames.GA_ABSOLUTE_OPEX,
      denominator: CalculateFieldNames.ANNUAL_REVENUE
    },
    true
  );

  return result;
};

export const calculateSMOpex: CalculateFunction = (values: FieldValues) => {
  const result = calculateDivision(
    values,
    {
      numerator: CalculateFieldNames.SM_ABSOLUTE_OPEX,
      denominator: CalculateFieldNames.ANNUAL_REVENUE
    },
    true
  );

  return result;
};

export const calculateRDOpex: CalculateFunction = (values: FieldValues) => {
  const result = calculateDivision(
    values,
    {
      numerator: CalculateFieldNames.RD_ABSOLUTE_OPEX,
      denominator: CalculateFieldNames.ANNUAL_REVENUE
    },
    true
  );

  return result;
};
